"use client"
import {
  Button,
  FormControl,
  FormLabel,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Select,
  Tooltip,
  useDisclosure,
  useToast,
  Box,
  SkeletonCircle,
  SkeletonText,
  Badge,
  FormErrorMessage,
} from "@chakra-ui/react"
import { useEffect, useState, useRef } from "react"
import { useDispatch, useSelector } from "react-redux"
import {
  addCertificationsAsync,
  deleteCertificateAsync,
  fetchCertificationsAsync,
  selectCertifications,
  updateCertificationAsync,
} from "./CertificationSlice"
import { useForm } from "react-hook-form"
import EmptyState from "../../components/Tester"
import { FaGraduationCap } from "react-icons/fa"

function Certifications() {
  const dispatch = useDispatch()
  const certifications = useSelector(selectCertifications)

  const [isAddLoading, setIsAddLoading] = useState(false)
  const [isUpdateLoading, setIsUpdateLoading] = useState(false)
  const [isDeleteLoading, setIsDeleteLoading] = useState(false)
  const [isInitialLoading, setIsInitialLoading] = useState(true)

  const {
    isOpen: isEditOpen,
    onOpen: onEditOpen,
    onClose: onEditClose,
  } = useDisclosure()
  const {
    isOpen: isAddOpen,
    onOpen: onAddOpen,
    onClose: onAddClose,
  } = useDisclosure()
  const {
    isOpen: isDeleteOpen,
    onOpen: onDeleteOpen,
    onClose: onDeleteClose,
  } = useDisclosure()

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    getValues,
    formState: { errors },
    watch,
  } = useForm({
    mode: "onChange",
    shouldUnregister: true, // unmounting fields (like validity) won't keep their values
  })

  const [selectedCertificate, setSelectedCertificate] = useState(null)
  const [deletingCertificate, setDeletingCertificate] = useState(null)
  const toast = useToast()

  const hasInitiatedFetch = useRef(false)

  // Helper to refresh the list and control skeleton visibility
  const refreshCertifications = async () => {
    setIsInitialLoading(true)
    try {
      await dispatch(fetchCertificationsAsync())
    } finally {
      setIsInitialLoading(false)
    }
  }

  useEffect(() => {
    if (!hasInitiatedFetch.current) {
      hasInitiatedFetch.current = true
      refreshCertifications()
    } else {
      setIsInitialLoading(false)
    }
  }, [dispatch])

  // Open Add with clean defaults
  const openAddModal = () => {
    reset({
      certification_name: "",
      certification_number: "",
      certification_url: "",
      certification_date: "",
      is_certification_expire: "",
      certification_validity: "",
    })
    onAddOpen()
  }

  // Open Edit with prefilled values using reset (avoids flickers and control issues)
  const openUpdateModal = (certificate) => {
    setSelectedCertificate(certificate)
    reset({
      certification_name: certificate?.certification_name || "",
      certification_number: certificate?.certification_number || "",
      certification_url: certificate?.certification_url || "",
      certification_date: certificate?.certification_date || "",
      is_certification_expire: certificate?.is_certification_expire ? "True" : "False",
      certification_validity:
        certificate?.is_certification_expire && certificate?.certification_validity
          ? certificate.certification_validity
          : "",
    })
    onEditOpen()
  }

  // Open Delete
  const openDeleteModal = (certificate) => {
    setDeletingCertificate(certificate)
    onDeleteOpen()
  }

  // Close handlers that also reset state
  const handleCloseAdd = () => {
    reset()
    onAddClose()
  }
  const handleCloseEdit = () => {
    setSelectedCertificate(null)
    reset()
    onEditClose()
  }
  const handleCloseDelete = () => {
    setDeletingCertificate(null)
    onDeleteClose()
  }

  // Validators
  const validateNotFutureDate = (value) => {
    if (!value) return "Certification Date is required"
    const d = new Date(value)
    const today = new Date()
    today.setHours(0, 0, 0, 0)
    return d <= today || "Certification date cannot be in the future"
  }

  const validateValidityDate = (value) => {
    const expire = watch("is_certification_expire")
    if (expire !== "True") return true
    if (!value) return "Certification validity is required"
    const validity = new Date(value)
    const certDateRaw = getValues("certification_date")
    if (!certDateRaw) return "Please provide Certification Date first"
    const certDate = new Date(certDateRaw)
    return validity > certDate || "Validity must be after Certification Date"
  }

  const handleSave = async (transformedData) => {
    if (isAddLoading) return
    setIsAddLoading(true)
    const newCertification = {
      ...transformedData,
      is_certification_expire: transformedData.is_certification_expire === "True",
    }
    try {
      await dispatch(addCertificationsAsync({ newCertification }))
      await refreshCertifications()
      handleCloseAdd()
      toast({
        title: "Certification Added",
        description: "Your certification has been successfully added.",
        status: "success",
        duration: 3000,
        isClosable: true,
        position: "top-right",
      })
    } catch (error) {
      console.error("Failed to add certification", error)
      toast({
        title: "Error",
        description: "Failed to add certification. Please try again.",
        status: "error",
        duration: 3000,
        isClosable: true,
        position: "top-right",
      })
    } finally {
      setIsAddLoading(false)
    }
  }

  const handleUpdate = async (transformedData) => {
    if (isUpdateLoading) return
    if (selectedCertificate) {
      setIsUpdateLoading(true)
      const certificateData = {
        ...transformedData,
        is_certification_expire: transformedData.is_certification_expire === "True",
      }
      try {
        await dispatch(
          updateCertificationAsync({
            uuid: selectedCertificate.uuid,
            certificateData,
          }),
        )
        await refreshCertifications()
        handleCloseEdit()
        toast({
          title: "Certification Updated",
          description: "Your certification details have been successfully updated.",
          status: "success",
          duration: 3000,
          isClosable: true,
          position: "top-right",
        })
      } catch (error) {
        console.error("Failed to update certification", error)
        toast({
          title: "Error",
          description: "Failed to update certification. Please try again.",
          status: "error",
          duration: 3000,
          isClosable: true,
          position: "top-right",
        })
      } finally {
        setIsUpdateLoading(false)
      }
    }
  }

  const handleDelete = async (uuid) => {
    if (isDeleteLoading) return
    setIsDeleteLoading(true)
    try {
      await dispatch(deleteCertificateAsync({ uuid }))
      await refreshCertifications()
      handleCloseDelete()
      toast({
        title: "Certification Deleted",
        description: "Your certification has been successfully deleted.",
        status: "success",
        duration: 3000,
        isClosable: true,
        position: "top-right",
      })
    } catch (error) {
      console.error("Failed to delete certification", error)
      toast({
        title: "Error",
        description: "Failed to delete certification. Please try again.",
        status: "error",
        duration: 3000,
        isClosable: true,
        position: "top-right",
      })
    } finally {
      setIsDeleteLoading(false)
    }
  }

  return (
    <div className="bg-mainBg" style={{ fontFamily: "Inter, sans-serif" }}>
      <div className=" mx-auto">
        <div className="bg-white rounded-xl w-full px-3 sm:px-5 py-4 shadow-sm  border-gray-100">
          {/* Header */}
          <div className="flex sm:flex-row sm:items-center justify-between gap-3 mb-3">
            <div className="flex items-start gap-2">
              <div className="h-9 w-9 rounded-lg bg-blue-50 text-blue-600 flex items-center justify-center">
                <FaGraduationCap />
              </div>
              <div>
                <h1 className="text-xl sm:text-2xl md:text-3xl text-left font-semibold">Certifications</h1>
                <p className="text-xs text-gray-500">Add and manage your certifications</p>
              </div>
            </div>
            <div className="flex gap-2">
              <Button onClick={openAddModal} colorScheme="blue" className="whitespace-nowrap shadow">
                Add <i className="fa-solid fa-plus pl-2"></i>
              </Button>
            </div>
          </div>

          {/* Content */}
          <div className="space-y-3">
            {isInitialLoading ? (
              <>
                <Box className="border p-4 sm:p-5 bg-white shadow-sm rounded-lg">
                  <SkeletonCircle size="14" mb="4" />
                  <SkeletonText noOfLines={4} spacing="3" skeletonHeight="3" />
                </Box>
                <Box className="border p-4 sm:p-5 bg-white shadow-sm rounded-lg">
                  <SkeletonCircle size="14" mb="4" />
                  <SkeletonText noOfLines={4} spacing="3" skeletonHeight="3" />
                </Box>
              </>
            ) : Array.isArray(certifications) && certifications.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-6">
                <EmptyState
                  title="Certifications"
                  icon={FaGraduationCap}
                  emptyMessage="Start by adding your Certifications"
                  addButtonText="Add Your Certifications"
                  onAdd={openAddModal}
                />
              </div>
            ) : (
              certifications &&
              certifications.map((certificate) => {
                const expires = certificate?.is_certification_expire === true
                return (
                  <div
                    key={certificate.uuid}
                    className="group relative border rounded-xl shadow-sm hover:shadow-md transition-all duration-200 bg-white overflow-hidden"
                  >
                    {/* Accent bar */}
                    <div className="absolute left-0 top-0 h-full w-1 bg-gradient-to-b from-blue-500 to-indigo-500" />
                    <div className="p-4 md:p-5">
                      {/* Header + actions */}
                      <div className="flex justify-between items-start gap-3 mb-2">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <div className="text-base sm:text-lg font-semibold text-gray-900 leading-tight break-words uppercase">
                              {certificate.certification_name}
                            </div>
                            <Badge colorScheme={expires ? "red" : "green"} variant="subtle" className="rounded-full">
                              {expires ? "Expires" : "No Expiry"}
                            </Badge>
                          </div>
                        </div>
                        <div className="flex gap-2 flex-shrink-0">
                          <Tooltip label="Edit Certificate">
                            <Button
                              onClick={() => openUpdateModal(certificate)}
                              colorScheme="blue"
                              className="h-10 w-10 p-0 flex items-center justify-center shadow-sm"
                            >
                              <i className="fa-sharp fa-solid fa-pen"></i>
                            </Button>
                          </Tooltip>
                          <Tooltip label="Delete Certificate">
                            <Button
                              onClick={() => openDeleteModal(certificate)}
                              colorScheme="red"
                              variant="outline"
                              className="h-10 w-10 p-0 flex items-center justify-center"
                            >
                              <i className="cursor-pointer fa-solid fa-trash"></i>
                            </Button>
                          </Tooltip>
                        </div>
                      </div>

                      {/* Details */}
                      <div className="mt-1 space-y-2 text-sm">
                        {certificate?.certification_number && (
                          <div className="flex items-center flex-wrap text-gray-700">
                            <span className="font-medium mr-1">Certification Number:</span>
                            <span className="break-words">{certificate.certification_number}</span>
                          </div>
                        )}

                        {certificate?.certification_url && (
                          <div className="flex items-center flex-wrap text-gray-700">
                            <span className="font-medium mr-1">Certification URL:</span>
                            <a
                              href={certificate.certification_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-blue-600 hover:underline break-words"
                            >
                              {certificate.certification_url}
                            </a>
                          </div>
                        )}

                        {certificate?.certification_date && (
                          <div className="flex items-center flex-wrap text-gray-700">
                            <span className="font-medium mr-1">Certification Date:</span>
                            <span className="text-gray-800 font-semibold">{certificate.certification_date}</span>
                          </div>
                        )}

                        {certificate?.is_certification_expire !== undefined && (
                          <div className="flex items-center flex-wrap text-gray-700">
                            <span className="font-medium mr-1">Certification Expiry:</span>
                            <span className="text-gray-800 font-semibold">
                              {certificate.is_certification_expire ? "Yes" : "No"}
                            </span>
                          </div>
                        )}

                        {certificate?.certification_validity && (
                          <div className="flex items-center flex-wrap text-gray-700">
                            <span className="font-medium mr-1">Certification Valid Till:</span>
                            <span className="text-gray-800 font-semibold">{certificate.certification_validity}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )
              })
            )}
          </div>
        </div>
      </div>

      {/* Edit Certification Modal */}
      <Modal isOpen={isEditOpen} onClose={handleCloseEdit} closeOnOverlayClick={false} isCentered={false}>
        <ModalOverlay />
        <ModalContent maxW="5xl">
          <ModalHeader>
            <div className="flex items-center gap-2">
              <div className="h-8 w-8 rounded-md bg-blue-50 text-blue-600 flex items-center justify-center">
                <FaGraduationCap />
              </div>
              <span>Edit Certification</span>
            </div>
          </ModalHeader>
          <ModalCloseButton />
          <div className="">
            {/* noValidate prevents native browser tooltips */}
            <form onSubmit={handleSubmit(handleUpdate)} noValidate>
              <ModalBody>
                <FormControl isInvalid={!!errors.certification_name} isRequired>
                  <FormLabel>Certification Name</FormLabel>
                  <Input
                    placeholder="Certification Name"
                    aria-invalid={!!errors.certification_name}
                    {...register("certification_name", {
                      required: "Certification Name is required",
                      minLength: { value: 3, message: "Must be at least 3 characters" },
                      maxLength: { value: 120, message: "Must be 120 characters or less" },
                      setValueAs: (v) => (typeof v === "string" ? v.trim() : v),
                    })}
                  />
                  <FormErrorMessage>{errors.certification_name && errors.certification_name.message}</FormErrorMessage>
                </FormControl>

                <FormControl mt={4} isInvalid={!!errors.certification_number} isRequired>
                  <FormLabel>Certification Number</FormLabel>
                  <Input
                    placeholder="Certification Number"
                    aria-invalid={!!errors.certification_number}
                    {...register("certification_number", {
                      required: "Certification Number is required",
                      minLength: { value: 3, message: "Must be at least 3 characters" },
                      maxLength: { value: 64, message: "Must be 64 characters or less" },
                      pattern: {
                        value: /^[A-Za-z0-9._/-]+$/,
                        message: "Only letters, numbers, dot, dash, slash allowed",
                      },
                      setValueAs: (v) => (typeof v === "string" ? v.trim() : v),
                    })}
                  />
                  <FormErrorMessage>
                    {errors.certification_number && errors.certification_number.message}
                  </FormErrorMessage>
                </FormControl>

                <FormControl mt={4} isInvalid={!!errors.certification_url}>
                  <FormLabel>Certification URL</FormLabel>
                  <Input
                    placeholder="Certification URL"
                    aria-invalid={!!errors.certification_url}
                    {...register("certification_url", {
                      setValueAs: (v) => (typeof v === "string" ? v.trim() : v),
                      pattern: {
                        value:
                          /^(https?:\/\/)?([\w-]+(\.[\w-]+)+)(:[0-9]{2,5})?([\w.,@?^=%&:/~+#-]*[\w@?^=%&/~+#-])?$/,
                        message: "Please enter a valid URL",
                      },
                    })}
                  />
                  <FormErrorMessage>{errors.certification_url && errors.certification_url.message}</FormErrorMessage>
                </FormControl>

                <FormControl mt={4} isInvalid={!!errors.certification_date} isRequired>
                  <FormLabel>Certification Date</FormLabel>
                  <Input
                    type="date"
                    placeholder="Certification Date"
                    aria-invalid={!!errors.certification_date}
                    {...register("certification_date", {
                      required: "Certification Date is required",
                      validate: validateNotFutureDate,
                    })}
                  />
                  <FormErrorMessage>
                    {errors.certification_date && errors.certification_date.message}
                  </FormErrorMessage>
                </FormControl>

                <FormControl mt={4} isInvalid={!!errors.is_certification_expire} isRequired>
                  <FormLabel>Does Certification Expire?</FormLabel>
                  <Select
                    placeholder="Select Certification Expiry"
                    aria-invalid={!!errors.is_certification_expire}
                    {...register("is_certification_expire", {
                      required: "Please select Yes or No",
                    })}
                    onChange={(e) => {
                      setValue("is_certification_expire", e.target.value)
                      if (e.target.value !== "True") {
                        setValue("certification_validity", "")
                      }
                    }}
                  >
                    <option value="True">Yes</option>
                    <option value="False">No</option>
                  </Select>
                  <FormErrorMessage>
                    {errors.is_certification_expire && errors.is_certification_expire.message}
                  </FormErrorMessage>
                </FormControl>

                {watch("is_certification_expire") === "True" && (
                  <FormControl mt={4} isInvalid={!!errors.certification_validity} isRequired>
                    <FormLabel>Certification Valid Till</FormLabel>
                    <Input
                      type="date"
                      placeholder="Enter Certification Validity"
                      aria-invalid={!!errors.certification_validity}
                      {...register("certification_validity", {
                        validate: validateValidityDate,
                      })}
                    />
                    <FormErrorMessage>
                      {errors.certification_validity && errors.certification_validity.message}
                    </FormErrorMessage>
                  </FormControl>
                )}
              </ModalBody>

              <ModalFooter className="bg-logoblue">
                <Button mr={3} type="submit" isLoading={isUpdateLoading} isDisabled={isUpdateLoading}>
                  Save
                </Button>
                <Button onClick={handleCloseEdit}>Cancel</Button>
              </ModalFooter>
            </form>
          </div>
        </ModalContent>
      </Modal>

      {/* Add Certification Modal */}
      <Modal isOpen={isAddOpen} onClose={handleCloseAdd}>
        <ModalOverlay />
        <ModalContent maxW="5xl">
          <ModalHeader>
            <div className="flex items-center gap-2">
              <div className="h-8 w-8 rounded-md bg-blue-50 text-blue-600 flex items-center justify-center">
                <FaGraduationCap />
              </div>
              <span>Certification Details</span>
            </div>
          </ModalHeader>
          <ModalCloseButton />
          {/* noValidate prevents native browser tooltips */}
          <form onSubmit={handleSubmit(handleSave)} noValidate>
            <ModalBody pb={6}>
              <FormControl isInvalid={!!errors.certification_name} isRequired>
                <FormLabel>Certification Name</FormLabel>
                <Input
                  placeholder="Certification Name"
                  aria-invalid={!!errors.certification_name}
                  {...register("certification_name", {
                    required: "Certification Name is required",
                    minLength: { value: 3, message: "Must be at least 3 characters" },
                    maxLength: { value: 120, message: "Must be 120 characters or less" },
                    setValueAs: (v) => (typeof v === "string" ? v.trim() : v),
                  })}
                />
                <FormErrorMessage>{errors.certification_name && errors.certification_name.message}</FormErrorMessage>
              </FormControl>

              <FormControl mt={4} isInvalid={!!errors.certification_number} isRequired>
                <FormLabel>Certification Number</FormLabel>
                <Input
                  placeholder="Certification Number"
                  aria-invalid={!!errors.certification_number}
                  {...register("certification_number", {
                    required: "Certification Number is required",
                    minLength: { value: 3, message: "Must be at least 3 characters" },
                    maxLength: { value: 64, message: "Must be 64 characters or less" },
                    pattern: {
                      value: /^[A-Za-z0-9._/-]+$/,
                      message: "Only letters, numbers, dot, dash, slash allowed",
                    },
                    setValueAs: (v) => (typeof v === "string" ? v.trim() : v),
                  })}
                />
                <FormErrorMessage>{errors.certification_number && errors.certification_number.message}</FormErrorMessage>
              </FormControl>

              <FormControl mt={4} isInvalid={!!errors.certification_url}>
                <FormLabel>Certification URL</FormLabel>
                <Input
                  placeholder="Certification URL"
                  aria-invalid={!!errors.certification_url}
                  {...register("certification_url", {
                    setValueAs: (v) => (typeof v === "string" ? v.trim() : v),
                    pattern: {
                      value:
                        /^(https?:\/\/)?([\w-]+(\.[\w-]+)+)(:[0-9]{2,5})?([\w.,@?^=%&:/~+#-]*[\w@?^=%&/~+#-])?$/,
                      message: "Please enter a valid URL",
                    },
                  })}
                />
                <FormErrorMessage>{errors.certification_url && errors.certification_url.message}</FormErrorMessage>
              </FormControl>

              <FormControl mt={4} isInvalid={!!errors.certification_date} isRequired>
                <FormLabel>Certification Date</FormLabel>
                <Input
                  type="date"
                  placeholder="Certification Date"
                  aria-invalid={!!errors.certification_date}
                  {...register("certification_date", {
                    required: "Certification Date is required",
                    validate: validateNotFutureDate,
                  })}
                />
                <FormErrorMessage>{errors.certification_date && errors.certification_date.message}</FormErrorMessage>
              </FormControl>

              <FormControl mt={4} isInvalid={!!errors.is_certification_expire} isRequired>
                <FormLabel>Does Certification Expire?</FormLabel>
                <Select
                  placeholder="Select Certification Expiry"
                  aria-invalid={!!errors.is_certification_expire}
                  {...register("is_certification_expire", {
                    required: "Please select Yes or No",
                  })}
                  onChange={(e) => {
                    setValue("is_certification_expire", e.target.value)
                    if (e.target.value !== "True") {
                      setValue("certification_validity", "")
                    }
                  }}
                >
                  <option value="True">Yes</option>
                  <option value="False">No</option>
                </Select>
                <FormErrorMessage>
                  {errors.is_certification_expire && errors.is_certification_expire.message}
                </FormErrorMessage>
              </FormControl>

              {watch("is_certification_expire") === "True" && (
                <FormControl mt={4} isInvalid={!!errors.certification_validity} isRequired>
                  <FormLabel>Certification Valid Till</FormLabel>
                  <Input
                    type="date"
                    placeholder="Enter Certification Validity"
                    aria-invalid={!!errors.certification_validity}
                    {...register("certification_validity", {
                      validate: validateValidityDate,
                    })}
                  />
                  <FormErrorMessage>
                    {errors.certification_validity && errors.certification_validity.message}
                  </FormErrorMessage>
                </FormControl>
              )}
            </ModalBody>
            <ModalFooter className="bg-logoblue">
              <Button mr={3} type="submit" isLoading={isAddLoading} isDisabled={isAddLoading}>
                Save
              </Button>
              <Button onClick={handleCloseAdd}>Cancel</Button>
            </ModalFooter>
          </form>
        </ModalContent>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal isOpen={isDeleteOpen} onClose={handleCloseDelete}>
        <ModalOverlay />
        <ModalContent padding={3}>
          <ModalHeader>Confirm Deletion</ModalHeader>
          <ModalCloseButton color="gray.500" />
          <ModalBody>
            <h4 className="text-sm sm:text-base text-gray-800">
              Are you sure you want to delete
              <span className="font-semibold"> {deletingCertificate?.certification_name} </span>
              certification?
            </h4>
          </ModalBody>
          <ModalFooter gap={4}>
            <Button
              colorScheme="red"
              onClick={() => handleDelete(deletingCertificate?.uuid)}
              isLoading={isDeleteLoading}
              isDisabled={isDeleteLoading}
            >
              Delete
            </Button>
            <Button variant="outline" onClick={handleCloseDelete}>
              Cancel
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </div>
  )
}
export default Certifications